import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/apiAdmin';
import { adminSessionUsable } from '../../utils/authAdmin';
import { useAdminWebSocket } from '../../hooks/useProjectWebSocket';
import {
    parseCompositeDependencyKey,
    normaliseToSubtaskId,
    buildCompositeKey
} from '../../utils/idHelpers';
import { buildTemplateWeekendWorkMap } from '../../utils/templateWeekendWork';
import KanbanBoard from '../Client/components/KanbanBoard';
import TemplateGantt from '../Client/components/TemplateGantt';
import TaskDetailsModal from '../Client/components/TaskDetailsModal';
import CreateTaskModal from '../Client/components/CreateTaskModal';
import {
    FaArrowLeft,
    FaPlus,
    FaPuzzlePiece,
    FaTimes
} from 'react-icons/fa';
import EffortInput from '../../components/EffortInput';
import { formatEffort, sumEffort } from '../../utils/effort';
import { coreMainTasks } from '../../utils/effortDisplay';

const TemplateEditor = () => {
    const { templateId } = useParams();
    const navigate = useNavigate();

    // Main template data (includes add-in tasks with sourceAddinId)
    const [template, setTemplate] = useState(null);
    const [project, setProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [subtasks, setSubtasks] = useState([]);
    const [loading, setLoading] = useState(true);

    // Template effort estimate (T477). Estimate only - D5.
    const [editingTemplateEffort, setEditingTemplateEffort] = useState(false);
    const [savingTemplateEffort, setSavingTemplateEffort] = useState(false);

    // Add-in state - available add-ins from templates list (for attach dropdown)
    const [availableAddins, setAvailableAddins] = useState([]);
    const [showAddinDropdown, setShowAddinDropdown] = useState(false);
    const [attachingAddin, setAttachingAddin] = useState(null);
    const [detachingAddin, setDetachingAddin] = useState(null);

    // UI state - persist view mode
    const [currentView, setCurrentView] = useState(() => {
        return localStorage.getItem('templateEditorView') || 'kanban';
    });

    // Persist view mode changes
    useEffect(() => {
        localStorage.setItem('templateEditorView', currentView);
    }, [currentView]);
    const [selectedTask, setSelectedTask] = useState(null);
    const [savingField, setSavingField] = useState(null);
    const [savingChanges, setSavingChanges] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createInAddin, setCreateInAddin] = useState(null); // addinId for add-in task, null for main template
    const [createInSwimlane, setCreateInSwimlane] = useState(null);

    // Add-in selector for task creation (when add-ins attached)
    const [showAddinSelector, setShowAddinSelector] = useState(false);

    // Unmatched epic resolution dialog state
    const [unmatchedEpics, setUnmatchedEpics] = useState(null);

    // Kanban state - default Archived column to collapsed
    const [collapsedColumns, setCollapsedColumns] = useState(() => {
        const saved = localStorage.getItem(`collapsedTemplateEpics_${templateId}`);
        return saved ? new Set(JSON.parse(saved)) : new Set(['Archived']);
    });
    const [collapsedSwimlanes, setCollapsedSwimlanes] = useState(new Set());
    const [draggedTask, setDraggedTask] = useState(null);
    // ⛔ THE REF, NOT THE STATE, FEEDS handleDrop - the touch path calls
    // onDragStart and onDrop in the same tick, so state is one render stale
    // there. See Dashboard.js's twin comment.
    const draggedTaskRef = useRef(null);

    // Persist collapsed columns to localStorage
    useEffect(() => {
        localStorage.setItem(`collapsedTemplateEpics_${templateId}`, JSON.stringify([...collapsedColumns]));
    }, [collapsedColumns, templateId]);

    // Fetch main template data (includes all add-in tasks with sourceAddinId)
    const fetchTemplateData = useCallback(async () => {
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/templates/${templateId}/project`);
            if (response.ok) {
                const data = await response.json();
                setTemplate(data.template);
                setProject(data.project);
                setTasks(data.tasks || []);
                setSubtasks(data.subtasks || []);
            }
        } catch (err) {
            console.error('Failed to fetch template:', err);
        } finally {
            setLoading(false);
        }
    }, [templateId]);

    // Fetch size-compatible pool add-ins (for attach dropdown)
    const fetchAvailableAddins = useCallback(async () => {
        if (!template?.size) {
            console.warn('Template has no size configured - pool add-in filtering unavailable');
            return;
        }
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/templates/pool?size=${encodeURIComponent(template.size)}`
            );
            if (response.ok) {
                const data = await response.json();
                setAvailableAddins(data.pool || []);
            }
        } catch (err) {
            console.error('Failed to fetch pool add-ins:', err);
        }
    }, [template?.size]);

    // Attach an add-in to the main template
    const attachAddin = useCallback(async (addin) => {
        setAttachingAddin(addin.templateId);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/templates/${templateId}/attach-addin`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ addinId: addin.templateId })
                }
            );
            if (response.ok) {
                const data = await response.json();
                if (data.unmatchedEpics && data.unmatchedEpics.length > 0) {
                    setUnmatchedEpics(data.unmatchedEpics);
                }
                await fetchTemplateData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to attach add-in:', response.status, errorData);
                alert(`Failed to attach add-in: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to attach add-in:', err);
            alert(`Failed to attach add-in: ${err.message}`);
        } finally {
            setAttachingAddin(null);
            setShowAddinDropdown(false);
        }
    }, [templateId, fetchTemplateData]);

    // Detach an add-in from the main template (tasks remain, just hidden)
    const detachAddin = useCallback(async (addinId) => {
        setDetachingAddin(addinId);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/templates/${templateId}/detach-addin`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ addinId })
                }
            );
            if (response.ok) {
                // Refresh template data to get updated addins array (tasks now filtered)
                await fetchTemplateData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to detach add-in:', response.status, errorData);
                alert(`Failed to detach add-in: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to detach add-in:', err);
            alert(`Failed to detach add-in: ${err.message}`);
        } finally {
            setDetachingAddin(null);
        }
    }, [templateId, fetchTemplateData]);

    // Get attached add-ins from project.addins array
    const attachedAddins = useMemo(() => {
        return (project?.addins || []).filter(a => a.attached);
    }, [project]);

    // Ids this editor is showing: the main template plus attached add-ins.
    // A ref, not state - the WS handler reads it without re-subscribing.
    const liveTemplateIdsRef = useRef(new Set());
    useEffect(() => {
        liveTemplateIdsRef.current = new Set([
            templateId,
            ...attachedAddins.map(a => a.addinId)
        ]);
    }, [templateId, attachedAddins]);

    // Suppress WS refetch while our own write is in flight - the explicit
    // await fetchTemplateData() after each operation already covers us, and
    // a mid-save refetch would fight the optimistic UI.
    const wsBusyRef = useRef(false);
    useEffect(() => {
        wsBusyRef.current = Boolean(savingField || savingChanges || attachingAddin || detachingAddin);
    }, [savingField, savingChanges, attachingAddin, detachingAddin]);

    // D7 (LIV-16c): the task details window fetches its OWN linked scripts, and
    // `scripts_updated` reaches neither of its hosts. TemplateEditor is the one
    // that gets it - a counter it bumps, which the modal adds to the dependency
    // array of the effect that already calls fetchTaskScripts.
    //
    // ⚠️ ClientView is deliberately left alone: it holds no __admin__
    // connection and is not given one, because that would put a second socket
    // on the busiest page in the admin portal for a panel that is read far more
    // often than it changes. The limit is documented, or it reads as a bug.
    const [scriptsRefreshKey, setScriptsRefreshKey] = useState(0);

    useAdminWebSocket(useCallback(async (message) => {
        if (message.type === 'scripts_updated') {
            // GATE 0 after the type, and only here: the bump makes the task
            // window refetch through apiFetch, whose 401 path redirects the tab.
            // No other gate: the modal decides whether it has anything to fetch.
            if (!await adminSessionUsable()) return;
            setScriptsRefreshKey(key => key + 1);
            return;
        }
        if (message.type !== 'template_updated') return;
        if (!liveTemplateIdsRef.current.has(message.templateId)) return;
        if (wsBusyRef.current) return;
        fetchTemplateData();
    }, [fetchTemplateData]));

    useEffect(() => {
        fetchTemplateData();
    }, [fetchTemplateData]);

    useEffect(() => {
        fetchAvailableAddins();
    }, [fetchAvailableAddins]);

    // Combined tasks - all tasks (including add-in tasks) come from main template
    // Add-in tasks have sourceAddinId field set
    /**
     * Save the template's typed effort estimate (T477).
     *
     * The same route and the same layer function a live project uses - a
     * template's container row lives in the PROJECTS table under
     * `projectId = templateId`, so `setProjectEffortEstimate` serves both.
     */
    const handleTemplateEffortSave = async (value) => {
        setSavingTemplateEffort(true);
        try {
            await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/project-effort/${templateId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ effortEstimated: value })
                }
            );
            setEditingTemplateEffort(false);
            await fetchTemplateData();
        } catch (err) {
            console.error('Failed to save template effort:', err);
        } finally {
            setSavingTemplateEffort(false);
        }
    };

    /**
     * The template's effort line: its typed estimate and the rollup of its main
     * tasks' estimates. NO ACTUAL AND NO VARIANCE (D5) - a template is never
     * worked on, so there is nothing to be over.
     *
     * ⛔ `coreMainTasks` matters more here than anywhere else: this page is
     * where `_isAddin` is genuinely computed, so an attached add-in's tasks sit
     * in `combinedTasks` ready to be summed into the host template's total by
     * accident. The selector excludes them on `sourceAddinId` (D23), and the
     * client's own swimlane on the lane (D1, ECL-01) - a template has a `Team`
     * lane too, and its tasks seed every project built from it.
     */
    const renderTemplateEffort = () => {
        // ⚠️ `combinedTasks`, NOT `tasks` - both names exist in this file, and
        // the wrong one compiles and silently rolls up the wrong set.
        const rows = coreMainTasks(combinedTasks, { teamSwimlaneId, project });
        const estimates = sumEffort(rows, 'effortEstimated');
        const typed = project?.effortEstimated;

        return (
            <span className="ml-3 inline-flex items-center gap-1.5 text-xs text-gray-600">
                <span title="Estimate typed on this template">Est {formatEffort(typed) ?? '-'}</span>
                <span className="text-gray-300">·</span>
                <span title="Sum of this template's main-task estimates">
                    Planned {formatEffort(estimates.total)}
                </span>
                {estimates.unestimated > 0 && (
                    <span
                        className="text-amber-600"
                        title="Main tasks with no estimate - the Planned total does not include them"
                    >
                        · {estimates.unestimated} unestimated
                    </span>
                )}
                <button
                    onClick={() => setEditingTemplateEffort(!editingTemplateEffort)}
                    className="text-gray-400 hover:text-primary-600 underline"
                    aria-label="Set template effort"
                >
                    edit
                </button>
                {editingTemplateEffort && (
                    <EffortInput
                        value={typed}
                        saving={savingTemplateEffort}
                        onCommit={handleTemplateEffortSave}
                    />
                )}
            </span>
        );
    };

    const combinedTasks = useMemo(() => {
        const mainSwimlanes = project?.swimlanes || [];
        const teamSwimlane = mainSwimlanes.find(s => s.name === 'Team');

        // Map tasks to include visual indicators for add-in tasks
        const allTasks = tasks.map(t => {
            const addinInfo = t.sourceAddinId
                ? attachedAddins.find(a => a.addinId === t.sourceAddinId)
                : null;

            // Determine if task is in Team swimlane
            const taskSwimlane = mainSwimlanes.find(s =>
                String(s.swimlaneId) === String(t.swimlaneId)
            );
            const isTeamSwimlane = taskSwimlane?.name === 'Team';

            return {
                ...t,
                projectId: t.sourceAddinId || templateId,
                _sourceTemplateId: t.sourceAddinId || templateId,
                _sourceTemplate: addinInfo || template,
                _isAddin: !!t.sourceAddinId,
                _addinColour: addinInfo?.colour,
                _addinAbbreviation: addinInfo?.abbreviation,
                _isTeamSwimlane: isTeamSwimlane
            };
        });

        // Apply stored global order by updating task.order field
        // Gantt sorts by task.order, so we set this from combinedMainTaskOrder
        const storedOrder = project?.combinedMainTaskOrder || {};

        return allTasks.map(task => {
            const taskKey = `${task._sourceTemplateId}:${task.taskId}`;
            const epicOrder = storedOrder[task.epicId] || [];
            const storedIndex = epicOrder.indexOf(taskKey);

            if (storedIndex !== -1) {
                // Task is in stored order - use stored position
                return { ...task, order: storedIndex };
            } else {
                // Task not in stored order - keep original order offset after stored ones
                return { ...task, order: (task.order || 0) + 1000 };
            }
        });
    }, [tasks, attachedAddins, template, templateId, project]);

    // Combined subtasks - all subtasks (including add-in subtasks) come from main template
    // Add-in subtasks have sourceAddinId field set
    const combinedSubtasks = useMemo(() => {
        const mainSwimlanes = project?.swimlanes || [];

        return subtasks.map(st => {
            const addinInfo = st.sourceAddinId
                ? attachedAddins.find(a => a.addinId === st.sourceAddinId)
                : null;

            // Find parent task to get swimlane (disambiguate by sourceAddinId to avoid ID collisions)
            const parentTask = tasks.find(t => {
                if (st.sourceAddinId) {
                    return t.taskId === st.mainTaskId && t.sourceAddinId === st.sourceAddinId;
                }
                return t.taskId === st.mainTaskId && !t.sourceAddinId;
            });
            const parentSwimlane = mainSwimlanes.find(s =>
                String(s.swimlaneId) === String(parentTask?.swimlaneId)
            );

            return {
                ...st,
                projectId: st.sourceAddinId || templateId,
                _sourceTemplateId: st.sourceAddinId || templateId,
                _sourceTemplate: addinInfo || template,
                _isAddin: !!st.sourceAddinId,
                _addinColour: addinInfo?.colour,
                _addinAbbreviation: addinInfo?.abbreviation,
                _isTeamSwimlane: parentSwimlane?.name === 'Team'
            };
        });
    }, [subtasks, tasks, attachedAddins, template, templateId, project]);

    // Resolve API-routing projectId for a task based on its source
    const resolveProjectId = useCallback((task) => {
        return task?.sourceAddinId ? task.sourceAddinId : templateId;
    }, [templateId]);

    // Weekend work map for TemplateGantt, keyed `${projectId}:${taskId}` -
    // see templateWeekendWork.js for why the key is composite.
    const templateWeekendWork = useMemo(
        () => buildTemplateWeekendWorkMap(combinedSubtasks),
        [combinedSubtasks]
    );

    // Save weekend work to the OWNING template (main template or add-in pool).
    // Accepts (subtaskId, dateKey, worked, projectId) or
    // (subtaskId, updates[], null, projectId) - same shape as ClientView's
    // saveWeekendWork plus the owning projectId from TemplateGantt.
    const handleWeekendWorkChange = async (subtaskId, dateKeyOrUpdates, worked, taskProjectId) => {
        const strId = String(subtaskId);
        const owningId = taskProjectId || templateId;
        const updates = Array.isArray(dateKeyOrUpdates)
            ? dateKeyOrUpdates
            : [{ dateKey: dateKeyOrUpdates, worked }];
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/weekend-work/${owningId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ subtaskId: strId, updates })
                }
            );
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                alert(`Failed to save weekend work: ${errorData.error || response.statusText}`);
                return;
            }
            fetchTemplateData();
        } catch (err) {
            console.error('Failed to save weekend work:', err);
            alert(`Failed to save weekend work: ${err.message}`);
        }
    };

    // Find task by ID, using optional projectId to disambiguate ID collisions across templates
    const findTask = useCallback((taskId, projectId) => {
        if (projectId) {
            const mainMatch = combinedTasks.find(t => t.taskId === taskId && t.projectId === projectId);
            if (mainMatch) return mainMatch;
            return combinedSubtasks.find(st => st.taskId === taskId && st.projectId === projectId);
        }
        // No projectId provided - warn if multiple matches exist
        const allMatches = [
            ...combinedTasks.filter(t => t.taskId === taskId),
            ...combinedSubtasks.filter(st => st.taskId === taskId)
        ];
        if (allMatches.length > 1) {
            console.warn(`findTask: ambiguous lookup for ${taskId} - ${allMatches.length} matches, returning first`);
        }
        return allMatches[0];
    }, [combinedTasks, combinedSubtasks]);

    // Use main template's epics (add-in tasks are mapped to these)
    const combinedEpics = useMemo(() => project?.epics || [], [project]);
    const combinedSwimlanes = useMemo(() => project?.swimlanes || [], [project]);

    // Get Team swimlane ID for client task identification
    const teamSwimlane = project?.swimlanes?.find(s => s.name === 'Team');
    const teamSwimlaneId = teamSwimlane?.swimlaneId;

    // Build links array from subtask dependencies
    // Handles composite dependency keys (tmpl_MAIN123:15) for cross-template deps
    const links = useMemo(() => {
        const linksList = [];
        combinedSubtasks.forEach(subtask => {
            if (subtask.dependsOn) {
                const deps = typeof subtask.dependsOn === 'string'
                    ? subtask.dependsOn.split(',').map(d => d.trim()).filter(d => d)
                    : subtask.dependsOn;

                deps.forEach(dep => {
                    // Parse composite key format: tmpl_MAIN123:15
                    let parsed = parseCompositeDependencyKey(dep);

                    // Handle simple numeric format for backward compatibility
                    if (!parsed) {
                        const numericRef = parseInt(dep, 10);
                        if (!isNaN(numericRef) && numericRef > 0) {
                            parsed = { templateId: templateId, numericRef };
                        }
                    }

                    if (!parsed) return;

                    const { templateId: sourceTemplateId, numericRef } = parsed;

                    // Use taskId for target - getNumericRef parses "ST#0015" correctly
                    linksList.push({
                        id: `${subtask.taskId}->${dep}`,
                        source: numericRef,
                        target: subtask.taskId,
                        type: 'FS',
                        color: subtask.dependencyColours?.[dep],
                        sourceProjectId: sourceTemplateId,
                        targetProjectId: subtask.sourceAddinId ? subtask.sourceAddinId : templateId
                    });
                });
            }
        });
        return linksList;
    }, [combinedSubtasks, templateId]);

    // Build blockedTasksMap from incomplete FS dependencies
    // Handles composite dependency keys (tmpl_MAIN123:15) for cross-template deps
    const blockedTasksMap = useMemo(() => {
        const map = new Map();
        combinedSubtasks.forEach(subtask => {
            if (subtask.dependsOn && !subtask.isClosed) {
                const deps = typeof subtask.dependsOn === 'string'
                    ? subtask.dependsOn.split(',').map(d => d.trim()).filter(d => d)
                    : subtask.dependsOn;

                const blockingDeps = [];
                deps.forEach(dep => {
                    // Parse composite key format: tmpl_MAIN123:15
                    let parsed = parseCompositeDependencyKey(dep);

                    // Handle simple numeric format for backward compatibility
                    if (!parsed) {
                        const numericRef = parseInt(dep, 10);
                        if (!isNaN(numericRef) && numericRef > 0) {
                            parsed = { templateId: templateId, numericRef };
                        }
                    }

                    if (!parsed) return;

                    const { templateId: sourceTemplateId, numericRef } = parsed;

                    // Convert numeric ref to full subtask ID
                    const targetTaskId = normaliseToSubtaskId(numericRef);

                    // Find the source task by matching templateId and taskId
                    const depTask = combinedSubtasks.find(st => {
                        const stTemplateId = st.sourceAddinId ? st.sourceAddinId : templateId;
                        return st.taskId === targetTaskId && stTemplateId === sourceTemplateId;
                    });

                    // FS dependency: blocked if predecessor not closed
                    if (depTask && !depTask.isClosed) {
                        const parentTask = combinedTasks.find(t => {
                            const tTemplateId = t.sourceAddinId ? t.sourceAddinId : templateId;
                            return t.taskId === depTask.mainTaskId && tTemplateId === sourceTemplateId;
                        });
                        blockingDeps.push({
                            type: 'dependency',
                            id: dep,
                            title: depTask.subject,
                            parentTitle: parentTask?.subject
                        });
                    }
                });

                if (blockingDeps.length > 0) {
                    // Build composite key for the blocked task
                    const blockedTaskKey = buildCompositeKey(subtask, templateId);
                    if (blockedTaskKey) {
                        map.set(blockedTaskKey, blockingDeps);
                    }
                }
            }
        });
        return map;
    }, [combinedSubtasks, combinedTasks, templateId]);

    // Build columns for Kanban (same as epics but formatted)
    const columns = useMemo(() => {
        // ⚠️ ALLOW-LIST - the F8 trap on the display side (2026-09-01). This map
        // is what KanbanBoard sees as `column`, so a field left out here is
        // invisible on every column header however faithfully it is stored.
        // Dropping effortEstimated made the epic effort control look dead: the
        // save landed, the refetch ran, and the header still read "Est -".
        return combinedEpics.map(epic => ({
            epicId: epic.epicId,
            name: epic.name,
            effortEstimated: epic.effortEstimated
        }));
    }, [combinedEpics]);

    // Task click handler - supports composite key format "templateId:taskId"
    const handleTaskClick = (taskIdOrComposite) => {
        let task;
        if (taskIdOrComposite.includes(':')) {
            // Composite key format: "templateId:taskId"
            const [sourceTemplateId, taskId] = taskIdOrComposite.split(':');
            task = combinedTasks.find(t => t._sourceTemplateId === sourceTemplateId && t.taskId === taskId) ||
                   combinedSubtasks.find(st => st._sourceTemplateId === sourceTemplateId && st.taskId === taskId);
        } else {
            // No composite key - default to main template only (never match add-in tasks without explicit templateId)
            task = combinedTasks.find(t => t._sourceTemplateId === templateId && t.taskId === taskIdOrComposite) ||
                   combinedSubtasks.find(st => st._sourceTemplateId === templateId && st.taskId === taskIdOrComposite);
        }
        if (task) {
            setSelectedTask(task);
        }
    };

    // Create task - show selector if add-ins attached, else open modal directly
    const handleCreateTask = (swimlaneId) => {
        setCreateInSwimlane(swimlaneId);
        if (attachedAddins.length > 0) {
            // Multiple templates available - show selector
            setShowAddinSelector(true);
        } else {
            // Only main template - open modal directly
            setCreateInAddin(null);
            setShowCreateModal(true);
        }
    };

    // Handle add-in selection for task creation
    const handleAddinSelect = (addinId) => {
        setCreateInAddin(addinId); // null for main template, addinId for add-in
        setShowAddinSelector(false);
        setShowCreateModal(true);
    };

    // Task save handler (for main tasks)
    // sourceAddinId: pool add-in projectId if creating add-in task, null for main template
    const handleTaskSave = async (taskData, sourceAddinId) => {
        // Lowest-order epic. Epics no longer carry isClosed.
        const defaultEpic = [...(project?.epics || [])].sort((a, b) => (a.order || 0) - (b.order || 0))[0];
        const targetProjectId = sourceAddinId ? sourceAddinId : templateId;
        const isAddinTask = !!sourceAddinId;

        // Pool add-in tasks send epicName, main template tasks send epicId
        const epicField = isAddinTask
            ? { epicName: defaultEpic?.name }
            : { epicId: Number(defaultEpic?.epicId) };

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...taskData,
                        // ISS-023: an add-in task never carries the HOST
                        // board's lane id - the add-in has its own lanes and
                        // the layer defaults to its Experts lane.
                        swimlaneId: isAddinTask ? undefined : taskData.swimlaneId,
                        projectId: targetProjectId,
                        ...epicField
                    })
                }
            );

            if (response.ok) {
                fetchTemplateData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to create task:', response.status, errorData);
                alert(`Failed to create task: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to save task:', err);
            alert(`Failed to save task: ${err.message}`);
        }
    };

    // Task update handler
    const handleTaskUpdate = async (taskId, updates, field, projectIdParam) => {
        // Disambiguate using projectIdParam, updates.projectId, or selectedTask context
        const disambiguator = projectIdParam ? projectIdParam :
            updates?.projectId ? updates.projectId :
            (selectedTask?.taskId === taskId ? selectedTask.projectId : undefined);
        const task = findTask(taskId, disambiguator);
        const targetProjectId = resolveProjectId(task);

        // Transform TemplateGantt's { relativeStart, relativeEnd } to Lambda fields
        const transformedUpdates = {};
        if (updates.relativeStart !== undefined) {
            transformedUpdates.relativeStart = updates.relativeStart;
        }
        if (updates.relativeEnd !== undefined) {
            transformedUpdates.relativeEnd = updates.relativeEnd;
        }
        // Pass through any other updates (excluding internal fields)
        Object.keys(updates).forEach(key => {
            if (!['relativeStart', 'relativeEnd', 'isSubtask', 'projectId'].includes(key)) {
                transformedUpdates[key] = updates[key];
            }
        });

        // Pool add-in main task column moves: send epicName instead of epicId
        if (task?._isAddin && transformedUpdates.epicId !== undefined) {
            const epic = combinedEpics.find(e => String(e.epicId) === String(transformedUpdates.epicId));
            if (epic) {
                transformedUpdates.epicName = epic.name;
                delete transformedUpdates.epicId;
            }
        }

        setSavingField(field);
        setSavingChanges(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        updates: transformedUpdates,
                        projectId: targetProjectId
                    })
                }
            );

            if (response.ok) {
                // Surface pool add-in reference sync results after a rename
                const result = await response.json().catch(() => null);
                if (result?.renameSync) {
                    const { updated, warnings } = result.renameSync;
                    const lines = [];
                    if (updated?.length > 0) {
                        lines.push(`Add-in references updated automatically (${updated.length}):`);
                        updated.forEach(u => lines.push(`- ${u.addinName} ${u.taskId} (${u.fields.join(', ')})`));
                    }
                    if (warnings?.length > 0) {
                        lines.push('Warnings:');
                        warnings.forEach(w => lines.push(`- ${w.message}`));
                    }
                    if (lines.length > 0) {
                        alert(lines.join('\n'));
                    }
                }
                await fetchTemplateData();
                if (selectedTask && selectedTask.taskId === taskId) {
                    setSelectedTask(prev => prev ? { ...prev, ...transformedUpdates } : prev);
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to update task:', response.status, errorData);
                alert(`Failed to update task: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to update task:', err);
            alert(`Failed to update task: ${err.message}`);
        } finally {
            setSavingField(null);
            setSavingChanges(false);
        }
    };

    // Parent task move handler (moves all subtasks by deltaDays)
    const handleParentTaskMove = async (parentTaskId, deltaDays, subtasksToMove) => {
        if (deltaDays === 0 || subtasksToMove.length === 0) return;

        const failures = [];

        for (const subtask of subtasksToMove) {
            const origStart = subtask.start;
            const origEnd = subtask.end;

            if (origStart == null || origEnd == null) continue;

            // Integer arithmetic - shift relative day offsets
            const newStart = origStart + deltaDays;
            const newEnd = origEnd + deltaDays;

            // Determine correct projectId for this subtask (disambiguate by Gantt row projectId)
            const subtaskId = subtask.originalId ? subtask.originalId : subtask.taskId;
            const subtaskData = findTask(subtaskId, subtask.projectId);
            const subtaskProjectId = resolveProjectId(subtaskData);

            try {
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(subtaskId)}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            updates: {
                                relativeStart: newStart,
                                relativeEnd: newEnd
                            },
                            projectId: subtaskProjectId
                        })
                    }
                );
                if (!response.ok) {
                    failures.push(subtask.taskId || subtask.originalId);
                }
            } catch (err) {
                console.error('Failed to move subtask:', subtask.taskId, err);
                failures.push(subtask.taskId || subtask.originalId);
            }
        }

        if (failures.length > 0) {
            alert(`Failed to move ${failures.length} subtask(s): ${failures.join(', ')}`);
        }

        fetchTemplateData();
    };

    // Task delete handler
    const handleTaskDelete = async (taskId) => {
        // Use selectedTask for disambiguation (called from task details modal)
        const disambiguator = selectedTask?.taskId === taskId ? selectedTask.projectId : undefined;
        const task = findTask(taskId, disambiguator);
        const targetProjectId = resolveProjectId(task);

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}`,
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ projectId: targetProjectId })
                }
            );

            if (response.ok) {
                setSelectedTask(null);
                fetchTemplateData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to delete task:', response.status, errorData);
                alert(`Failed to delete task: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to delete task:', err);
            alert(`Failed to delete task: ${err.message}`);
        }
    };

    // Subtask create handler
    // sourceAddinId: pool add-in projectId if parent is add-in, null for main template
    // Note: sourceAddinId not sent in request body - backend routes by projectId,
    // composite view adds sourceAddinId at read time
    const handleSubtaskCreate = async (mainTaskId, subtaskData, projectId, sourceAddinId) => {
        const targetProjectId = sourceAddinId ? sourceAddinId : templateId;

        // Disambiguate parent task lookup using targetProjectId
        const parentTask = findTask(mainTaskId, targetProjectId);
        const isAddinParent = !!parentTask?._isAddin;
        const epicField = isAddinParent
            ? { epicName: parentTask?.epicName || combinedEpics.find(e => String(e.epicId) === String(parentTask?.epicId))?.name }
            : {};

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        subject: subtaskData.subject,
                        description: subtaskData.description,
                        assignedUsers: subtaskData.assignedUsers,
                        relativeStart: subtaskData.relativeStart,
                        relativeEnd: subtaskData.relativeEnd,
                        isMeeting: subtaskData.isMeeting,
                        meetingType: subtaskData.meetingType,
                        agenda: subtaskData.agenda,
                        projectId: targetProjectId,
                        mainTaskId: mainTaskId,
                        ...epicField
                    })
                }
            );

            if (response.ok) {
                fetchTemplateData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to create subtask:', response.status, errorData);
                alert(`Failed to create subtask: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to create subtask:', err);
            alert(`Failed to create subtask: ${err.message}`);
        }
    };

    // Dependency create handler
    const handleDependencyCreate = async ({ source, target, type, sourceProjectId, targetProjectId }) => {
        // Construct composite key if source and target are from different templates
        const isCrossTemplate = sourceProjectId && targetProjectId && sourceProjectId !== targetProjectId;
        const resolvedSource = isCrossTemplate
            ? `${sourceProjectId}:${source}`
            : String(source);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/dependency`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        source: resolvedSource,
                        target,
                        type: type || 'FS',
                        projectId: templateId
                    })
                }
            );
            if (response.ok) {
                fetchTemplateData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to create dependency:', response.status, errorData);
                alert(`Failed to create dependency: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to create dependency:', err);
            alert(`Failed to create dependency: ${err.message}`);
        }
    };

    // Dependency delete handler
    const handleDependencyDelete = async (link) => {
        // Reconstruct stored format for cross-template dependencies
        const isCrossTemplate = link.sourceProjectId && link.targetProjectId && link.sourceProjectId !== link.targetProjectId;
        const resolvedSource = isCrossTemplate
            ? `${link.sourceProjectId}:${link.source}`
            : String(link.source);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/dependency`,
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        source: resolvedSource,
                        target: link.target,
                        projectId: templateId
                    })
                }
            );
            if (response.ok) {
                fetchTemplateData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to delete dependency:', response.status, errorData);
                alert(`Failed to delete dependency: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to delete dependency:', err);
            alert(`Failed to delete dependency: ${err.message}`);
        }
    };

    // Dependency colour change handler
    const handleDependencyColorChange = async (link, newColor) => {
        // Reconstruct stored format for cross-template dependencies
        const isCrossTemplate = link.sourceProjectId && link.targetProjectId && link.sourceProjectId !== link.targetProjectId;
        const resolvedSourceId = isCrossTemplate
            ? `${link.sourceProjectId}:${link.source}`
            : String(link.source);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/dependency-color`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        projectId: templateId,
                        sourceId: resolvedSourceId,
                        targetId: link.target,
                        color: newColor
                    })
                }
            );
            if (response.ok) {
                fetchTemplateData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to change dependency colour:', response.status, errorData);
                alert(`Failed to change dependency colour: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to change dependency colour:', err);
            alert(`Failed to change dependency colour: ${err.message}`);
        }
    };

    // Status change handler (Ctrl+click in Gantt)
    const handleStatusChange = async (taskId, statusId, projectId) => {
        const task = findTask(taskId, projectId);
        const targetProjectId = resolveProjectId(task);

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        updates: { status: statusId },
                        isSubtask: true,
                        projectId: targetProjectId
                    })
                }
            );
            if (response.ok) {
                fetchTemplateData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to change status:', response.status, errorData);
                alert(`Failed to change status: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to change status:', err);
            alert(`Failed to change status: ${err.message}`);
        }
    };

    // Handle Kanban drag-drop between columns
    const handleDrop = async (columnId) => {
        // Shadows the state binding deliberately: the ref is written
        // synchronously by onDragStart, so it is correct even when the drop
        // arrives in the same tick (touch). The rest of the body is unchanged.
        const draggedTask = draggedTaskRef.current;
        if (!draggedTask) return;
        const isMainTask = draggedTask.type === 'maintask' || !draggedTask.mainTaskId;
        const targetProjectId = resolveProjectId(draggedTask);

        // Pool add-in main task column moves: send epicName instead of epicId
        let updates;
        if (isMainTask) {
            if (draggedTask._isAddin) {
                const epic = combinedEpics.find(e => String(e.epicId) === String(columnId));
                if (!epic) {
                    console.error('Target column not found for add-in drop:', columnId);
                    draggedTaskRef.current = null;
                    setDraggedTask(null);
                    return;
                }
                updates = { epicName: epic.name };
            } else {
                updates = { epicId: columnId };
            }
        } else {
            updates = { status: columnId };
        }

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(draggedTask.taskId)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        updates,
                        isSubtask: !isMainTask,
                        projectId: targetProjectId
                    })
                }
            );
            if (response.ok) {
                fetchTemplateData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to drop task:', response.status, errorData);
                alert(`Failed to move task: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('Failed to drop task:', err);
            alert(`Failed to move task: ${err.message}`);
        }
        draggedTaskRef.current = null;
        setDraggedTask(null);
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showAddinDropdown && !e.target.closest('.addin-dropdown')) {
                setShowAddinDropdown(false);
            }
            if (showAddinSelector && !e.target.closest('.addin-selector')) {
                setShowAddinSelector(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showAddinDropdown, showAddinSelector]);

    if (loading) {
        return (
            <div className="h-full flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                <p className="mt-4 text-gray-600">Opening Template Editor...</p>
            </div>
        );
    }

    if (!template || !project) {
        return (
            <div className="h-full flex items-center justify-center text-gray-500">
                Template not found
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-64px)] flex flex-col">
            {/* Editor Header */}
            <div className="bg-white border-b border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between">
                    {/* Left: Back + Title */}
                    <div className="flex items-center gap-4">
                        <div className="relative group">
                            <button
                                onClick={() => navigate('/admin/templates')}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <FaArrowLeft className="w-4 h-4 text-gray-600" />
                            </button>
                            <div className="absolute left-0 top-full mt-2 px-3 py-2 text-sm bg-gray-800 text-white rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50">
                                Back to Template Manager
                                <div className="absolute left-4 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800" />
                            </div>
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900">
                                Template: {template.name}
                            </h1>
                            <span className="text-sm text-gray-500">Template Editor</span>
                            {/* T477 - the template's effort. NO ACTUAL (D5): a
                                blueprint is never worked on, so there is nothing
                                to be over and no variance to show. */}
                            {renderTemplateEffort()}
                        </div>
                    </div>

                    {/* Centre: Add-in Manager */}
                    <div className="flex items-center gap-2">
                        {/* Main template badge */}
                        <span className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg font-medium text-sm">
                            {template.name}
                        </span>

                        {/* Attached add-in badges with x buttons */}
                        {attachedAddins.map(addin => (
                            <React.Fragment key={addin.addinId}>
                                <span className="text-gray-400 font-medium">+</span>
                                <span
                                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-medium"
                                    style={{
                                        backgroundColor: `${addin.colour}20`,
                                        color: addin.colour
                                    }}
                                >
                                    <span>{addin.abbreviation}</span>
                                    <button
                                        onClick={() => detachAddin(addin.addinId)}
                                        disabled={detachingAddin === addin.addinId}
                                        className="p-0.5 hover:bg-white/30 rounded"
                                    >
                                        {detachingAddin === addin.addinId ? (
                                            <svg className="animate-spin w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <FaTimes className="w-3 h-3" />
                                        )}
                                    </button>
                                </span>
                            </React.Fragment>
                        ))}

                        {/* Attach add-in dropdown */}
                        <div className="relative addin-dropdown">
                            <button
                                onClick={() => setShowAddinDropdown(!showAddinDropdown)}
                                className="flex items-center gap-1 px-2 py-1.5 border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 text-sm"
                            >
                                <FaPlus className="w-3 h-3" />
                                <span>Add-in</span>
                            </button>

                            {showAddinDropdown && (
                                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                                    <div className="p-2 border-b border-gray-100">
                                        <span className="text-xs font-medium text-gray-500 uppercase">
                                            Attach Add-in Template
                                        </span>
                                    </div>
                                    {!template?.size ? (
                                        <div className="p-3 text-sm text-amber-600">
                                            Template size not configured - cannot filter pool add-ins
                                        </div>
                                    ) : availableAddins.length === 0 ? (
                                        <div className="p-3 text-sm text-gray-500">
                                            No add-in templates available
                                        </div>
                                    ) : (
                                        <div className="max-h-60 overflow-y-auto">
                                            {availableAddins.map(addin => {
                                                const isAttached = attachedAddins.some(a => a.addinId === addin.templateId);
                                                const isAttaching = attachingAddin === addin.templateId;
                                                return (
                                                    <button
                                                        key={addin.templateId}
                                                        onClick={() => attachAddin(addin)}
                                                        disabled={isAttached || isAttaching}
                                                        className={`w-full px-3 py-2 text-left flex items-center gap-2 ${
                                                            isAttached || isAttaching
                                                                ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                                                                : 'hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {isAttaching ? (
                                                            <svg className="animate-spin w-4 h-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                        ) : (
                                                            <FaPuzzlePiece
                                                                className="w-4 h-4"
                                                                style={{ color: isAttached ? '#9CA3AF' : addin.colour }}
                                                            />
                                                        )}
                                                        <span className="flex-1">{addin.name}</span>
                                                        <span
                                                            className="px-1.5 py-0.5 text-xs font-bold rounded"
                                                            style={{
                                                                backgroundColor: isAttached ? '#E5E7EB' : `${addin.colour}20`,
                                                                color: isAttached ? '#9CA3AF' : addin.colour
                                                            }}
                                                        >
                                                            {addin.abbreviation}
                                                        </span>
                                                        {isAttaching && (
                                                            <span className="text-xs text-gray-400">Attaching...</span>
                                                        )}
                                                        {isAttached && !isAttaching && (
                                                            <span className="text-xs text-gray-400">Attached</span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: View Toggle */}
                    <div className="flex items-center gap-3">
                        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setCurrentView('kanban')}
                                className={`px-3 py-1.5 text-sm font-medium ${
                                    currentView === 'kanban'
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Kanban
                            </button>
                            <button
                                onClick={() => setCurrentView('gantt')}
                                className={`px-3 py-1.5 text-sm font-medium ${
                                    currentView === 'gantt'
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Gantt
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Editor Content */}
            <div className="flex-1 overflow-hidden">
                {currentView === 'kanban' ? (
                    <KanbanBoard
                        project={project}
                        tasks={[...combinedTasks, ...combinedSubtasks]}
                        columns={columns}
                        members={[]}
                        collapsedColumns={collapsedColumns}
                        setCollapsedColumns={setCollapsedColumns}
                        collapsedSwimlanes={collapsedSwimlanes}
                        setCollapsedSwimlanes={setCollapsedSwimlanes}
                        draggedTask={draggedTask}
                        onDragStart={(task) => { draggedTaskRef.current = task; setDraggedTask(task); }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        onTaskClick={handleTaskClick}
                        onCreateTaskClick={handleCreateTask}
                        isAdmin={true}
                        isAdminPortal={true}
                        readOnly={false}
                        isTemplateMode={true}
                        blockedTasksMap={blockedTasksMap}
                        teamSwimlaneId={teamSwimlaneId}
                        onEpicChanged={fetchTemplateData}
                    />
                ) : (
                    <TemplateGantt
                        tasks={combinedTasks.map(t => ({ ...t, statusId: t.epicId, _isAddin: t._isAddin, _addinColour: t._addinColour }))}
                        showEffort={true}
                        subtasks={combinedSubtasks}
                        links={links}
                        epics={[]}
                        statuses={combinedEpics.map(e => ({ id: e.epicId, name: e.name, order: e.order }))}
                        teamSwimlaneId={teamSwimlaneId}
                        onTaskClick={handleTaskClick}
                        onDependencyCreate={handleDependencyCreate}
                        onDependencyDelete={handleDependencyDelete}
                        onDependencyColorChange={handleDependencyColorChange}
                        onTaskUpdate={handleTaskUpdate}
                        onParentTaskMove={handleParentTaskMove}
                        onStatusChange={handleStatusChange}
                        onSubtaskOrderChange={async (parentId, newOrder, projectId) => {
                            const parentTask = findTask(parentId, projectId);
                            const orderProjectId = resolveProjectId(parentTask);
                            try {
                                const response = await apiFetch(
                                    `${process.env.REACT_APP_API_URL}/admin/subtask-order/${orderProjectId}`,
                                    {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                            mainTaskId: parentId,
                                            subtaskOrder: newOrder
                                        })
                                    }
                                );
                                if (!response.ok) {
                                    const errorData = await response.json().catch(() => ({}));
                                    console.error('Failed to reorder subtasks:', response.status, errorData);
                                    alert(`Failed to reorder subtasks: ${errorData.error || response.statusText}`);
                                    return;
                                }
                                fetchTemplateData();
                            } catch (err) {
                                console.error('Failed to reorder subtasks:', err);
                                alert(`Failed to reorder subtasks: ${err.message}`);
                            }
                        }}
                        onMainTaskOrderChange={async (epicId, newOrder) => {
                            try {
                                const response = await apiFetch(
                                    `${process.env.REACT_APP_API_URL}/admin/templates/${templateId}/main-task-order`,
                                    {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ epicId, taskOrder: newOrder })
                                    }
                                );
                                if (!response.ok) {
                                    const errorData = await response.json().catch(() => ({}));
                                    console.error('Failed to save main task order:', response.status, errorData);
                                    alert(`Failed to save main task order: ${errorData.error || response.statusText}`);
                                    return;
                                }
                                fetchTemplateData();
                            } catch (err) {
                                console.error('Failed to save main task order:', err);
                                alert(`Failed to save main task order: ${err.message}`);
                            }
                        }}
                        readOnly={false}
                        taskStatuses={project?.subtaskStatuses || []}
                        blockedTasksMap={blockedTasksMap}
                        members={[]}
                        weekendWork={templateWeekendWork}
                        onWeekendWorkChange={handleWeekendWorkChange}
                    />
                )}
            </div>

            {/* Task Details Modal */}
            {selectedTask && (
                <TaskDetailsModal
                    isOpen={true}
                    scriptsRefreshKey={scriptsRefreshKey}
                    task={selectedTask}
                    project={project}
                    tasks={[...combinedTasks, ...combinedSubtasks]}
                    members={[]}
                    columns={columns}
                    taskStatuses={project?.subtaskStatuses || []}
                    projectMembers={[]}
                    onClose={() => setSelectedTask(null)}
                    onSubtaskClick={(taskId) => {
                        // Disambiguate using selected task's projectId (subtask shares parent's template)
                        const task = findTask(taskId, selectedTask?.projectId);
                        if (task) setSelectedTask(task);
                    }}
                    isAdmin={true}
                    isAdminPortal={true}
                    onSubtaskCreate={(mainTaskId, subtaskData, projectId) => {
                        // Disambiguate parent lookup using selectedTask context
                        const parentTask = findTask(mainTaskId, selectedTask?.projectId);
                        handleSubtaskCreate(mainTaskId, subtaskData, projectId, parentTask?.sourceAddinId);
                    }}
                    onUpdate={handleTaskUpdate}
                    onDelete={handleTaskDelete}
                    onSave={() => {
                        fetchTemplateData();
                        setSelectedTask(null);
                    }}
                    isTemplateMode={true}
                    savingField={savingField}
                    savingChanges={savingChanges}
                    blockedTasksMap={blockedTasksMap}
                />
            )}

            {/* Create Task Modal */}
            {showCreateModal && (() => {
                const swimlane = project?.swimlanes?.find(s => String(s.swimlaneId) === String(createInSwimlane));
                const swimlaneName = swimlane?.name === 'Team' ? 'Client Team' : swimlane?.name || 'Unknown';
                const addinInfo = createInAddin ? attachedAddins.find(a => a.addinId === createInAddin) : null;
                const contextName = addinInfo ? `${addinInfo.abbreviation} - ${swimlaneName}` : swimlaneName;
                return (
                    <CreateTaskModal
                        isOpen={true}
                        projectName={contextName}
                        members={[]}
                        isAdmin={true}
                        isTemplateMode={true}
                        onClose={() => {
                            setShowCreateModal(false);
                            setCreateInAddin(null);
                            setCreateInSwimlane(null);
                        }}
                        onCreate={(taskData) => {
                            handleTaskSave({ ...taskData, swimlaneId: createInSwimlane }, createInAddin);
                            setShowCreateModal(false);
                            setCreateInAddin(null);
                            setCreateInSwimlane(null);
                        }}
                    />
                );
            })()}

            {/* Add-in Selector Modal (when add-ins attached) */}
            {showAddinSelector && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
                    <div className="addin-selector bg-white rounded-lg shadow-xl w-80 overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-900">
                                Create Task In
                            </h3>
                        </div>
                        <div className="p-2">
                            {/* Main template option */}
                            <button
                                onClick={() => handleAddinSelect(null)}
                                className="w-full px-3 py-2.5 text-left flex items-center gap-3 hover:bg-gray-50 rounded-lg"
                            >
                                <span className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <span className="text-xs font-bold text-gray-600">
                                        {template.name.substring(0, 2).toUpperCase()}
                                    </span>
                                </span>
                                <span className="flex-1 font-medium text-gray-900">{template.name}</span>
                                <span className="text-xs text-gray-500">Main</span>
                            </button>

                            {/* Add-in options */}
                            {attachedAddins.map(addin => (
                                <button
                                    key={addin.addinId}
                                    onClick={() => handleAddinSelect(addin.addinId)}
                                    className="w-full px-3 py-2.5 text-left flex items-center gap-3 hover:bg-gray-50 rounded-lg"
                                >
                                    <span
                                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                                        style={{ backgroundColor: `${addin.colour}20` }}
                                    >
                                        <span
                                            className="text-xs font-bold"
                                            style={{ color: addin.colour }}
                                        >
                                            {addin.abbreviation}
                                        </span>
                                    </span>
                                    <span className="flex-1 font-medium text-gray-900">{addin.name}</span>
                                    <span className="text-xs text-gray-500">Add-in</span>
                                </button>
                            ))}
                        </div>
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                            <button
                                onClick={() => {
                                    setShowAddinSelector(false);
                                    setCreateInSwimlane(null);
                                }}
                                className="w-full px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Unmatched Epic Resolution Dialog - informational only */}
            {unmatchedEpics && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-96 overflow-hidden">
                        <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-200">
                            <h3 className="text-sm font-semibold text-yellow-800">
                                Unmatched Column Names
                            </h3>
                            <p className="text-xs text-yellow-600 mt-1">
                                The attached add-in references column names not found in this template.
                                Tasks with these names will appear without a column assignment until
                                matching columns are created.
                            </p>
                        </div>
                        <div className="p-4">
                            <ul className="space-y-2">
                                {unmatchedEpics.map(epicName => (
                                    <li key={epicName} className="flex items-center gap-2 text-sm text-gray-700">
                                        <span className="w-2 h-2 bg-yellow-400 rounded-full flex-shrink-0" />
                                        <span className="font-medium">{epicName}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
                            <button
                                onClick={() => setUnmatchedEpics(null)}
                                className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TemplateEditor;
